import React, { forwardRef, useMemo } from 'react';

// This is a self-contained React component for the Truklex Payday Statement.
// To use this in your application, import it and pass a `statementData` object as a prop.
// Example: <PaydayStatement statementData={yourData} />
// The html2pdf.js script is now loaded dynamically by the component.

const PaydayStatement = forwardRef(({ data }, ref) => {
    // --- Dummy Data: Replace this with the actual prop data in your application ---
    const expenses = useMemo(() => {
        try {
            return data?.expenses ? JSON.parse(data.expenses) : [];
        } catch (e) {
            return [];
        }
    }, [data]);

    const gross_earning = data?.jobs?.reduce((sum, job) => sum + (Number(job?.job?.job_amount) || 0), 0)
    const total_expense = expenses?.reduce((sum, e) => sum + Number(e.amount), 0)


    const styles = {
        body: {
            fontFamily: "'Inter', sans-serif",
            backgroundColor: '#F8F9FA',
            padding: '2rem',
        },
        statementContainer: {
            maxWidth: '840px',
            margin: 'auto',
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '2.5rem',
            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        },
        downloadButton: {
            backgroundColor: '#1E40AF', // A nice blue for the download button
            color: 'white',
            padding: '0.75rem 1.5rem',
            border: 'none',
            borderRadius: '0.5rem',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            transition: 'background-color 0.2s',
        },
        truklexOrange: { color: '#F97316' },
        sectionHeader: {
            fontSize: '1.25rem',
            fontWeight: '700',
            color: '#111827',
            borderBottom: '2px solid #E5E7EB',
            paddingBottom: '0.75rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
        },
        infoCard: {
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            padding: '1.5rem',
            border: '1px solid #E5E7EB',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
        },
        tableModern: {
            width: '100%',
            fontSize: '0.875rem',
            borderCollapse: 'collapse',
        },
        tableModernThead: {
            backgroundColor: '#F97316',
            color: 'white',
        },
        tableModernTh: {
            padding: '0.75rem 1rem',
            textAlign: 'left',
            fontWeight: '600',
        },
        tableModernTd: {
            padding: '0.75rem 1rem',
            borderBottom: '1px solid #E5E7EB',
        },
    };


    return (
        <div style={styles.body} className='d-none'>
            {/* FontAwesome is used for icons. Ensure it's loaded in your project's index.html or globally. */}
            {/* <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css"> */}

            <div >
                <div ref={ref} style={styles.statementContainer}>
                    {/* Header Section */}
                    <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '2rem' }}>
                        <div>
                            <img src="./logo-original.svg" alt="Truklex Logo" style={{ height: '3.5rem' }} />
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <h1 style={{ fontSize: '2.25rem', fontWeight: '800', color: '#1F2937' }}>Payday Statement</h1>
                            <p style={{ color: '#6B7280', marginTop: '0.25rem' }}>ID: {data.id}</p>
                        </div>
                    </header>

                    {/* Company and Driver Info Section */}
                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', margin: '2rem 0' }}>
                        <div style={styles.infoCard}>
                            <h2 style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#6B7280', marginBottom: '0.75rem', letterSpacing: '0.05em' }}>COMPANY</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#374151' }}>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>Name:</strong> {data?.company?.name}</p>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>Email:</strong> {data?.company?.email}</p>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>Phone:</strong> {data?.company?.phone}</p>
                            </div>
                        </div>
                        <div style={styles.infoCard}>
                            <h2 style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#6B7280', marginBottom: '0.75rem', letterSpacing: '0.05em' }}>DRIVER</h2>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#374151' }}>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>Name:</strong> {data?.driver?.full_name}</p>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>Email:</strong> {data?.driver?.email}</p>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>Phone:</strong> {data?.driver?.phone}</p>
                                <p><strong style={{ fontWeight: '600', color: '#111827' }}>DL Number:</strong> {data?.driver?.dlNumber}</p>
                            </div>
                        </div>
                    </section>

                    {/* Payment Info Section */}
                    <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', margin: '2rem 0' }}>
                        <div style={styles.infoCard}>
                            <h2 style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#6B7280', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>PAYMENT DATE</h2>
                            <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#1F2937' }}>{data?.paid_date}</p>
                        </div>
                        <div style={styles.infoCard}>
                            <h2 style={{ fontSize: '0.875rem', fontWeight: 'bold', color: '#6B7280', marginBottom: '0.5rem', letterSpacing: '0.05em' }}>STATUS</h2>
                            <span style={{ fontSize: '1.125rem', fontWeight: 'bold', color: '#065F46', backgroundColor: '#D1FAE5', padding: '0.5rem 1rem', borderRadius: '9999px' }}>{data?.is_paid}</span>
                        </div>
                    </section>

                    {/* Financial Summary Section */}
                    <section style={{ backgroundColor: '#F97316', color: 'white', padding: '2rem', borderRadius: '0.75rem', margin: '2.5rem 0', display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#FED7AA', letterSpacing: '0.1em' }}>GROSS</h3>
                            <p style={{ fontSize: '2.25rem', fontWeight: 'bold' }}>${parseFloat(gross_earning)?.toFixed(2)}</p>
                        </div>
                        <div style={{ fontSize: '2.25rem', fontWeight: '100', color: '#FDBA74' }}>-</div>
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#FED7AA', letterSpacing: '0.1em' }}>EXPENSES</h3>
                            <p style={{ fontSize: '2.25rem', fontWeight: 'bold' }}>${parseFloat(total_expense)?.toFixed(2)}</p>
                        </div>
                        <div style={{ fontSize: '2.25rem', fontWeight: '100', color: '#FDBA74' }}>=</div>
                        <div style={{ textAlign: 'center' }}>
                            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'white', letterSpacing: '0.1em' }}>NET PAY</h3>
                            <p style={{ fontSize: '2.75rem', fontWeight: '800' }}>${parseInt(gross_earning-total_expense)?.toFixed(2)}</p>
                        </div>
                    </section>

                    {/* Jobs Details Section */}
                    <section style={{ marginTop: '3rem' }} className="avoid-break">
                        <h2 style={styles.sectionHeader}><i className="fas fa-truck" style={{ marginRight: '0.75rem', color: '#F97316' }}></i>Completed Jobs ({data?.jobs?.length})</h2>
                        <div style={{ ...styles.infoCard, padding: 0, overflowX: 'auto' }}>
                            <table style={styles.tableModern}>
                                <thead style={styles.tableModernThead}>
                                    <tr>
                                        <th style={{ ...styles.tableModernTh, borderTopLeftRadius: '0.75rem' }}>Completed</th>
                                        <th style={styles.tableModernTh}>Pickup</th>
                                        <th style={styles.tableModernTh}>Dropoff</th>
                                        <th style={styles.tableModernTh}>Broker</th>
                                        <th style={styles.tableModernTh}>RC Number</th>
                                        <th style={{ ...styles.tableModernTh, textAlign: 'right', borderTopRightRadius: '0.75rem' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data?.jobs?.length > 0 && data?.jobs.map((job, index) => (
                                        <tr key={job.id} style={index % 2 === 0 ? {} : { backgroundColor: '#F9FAFB' }}>
                                            <td style={styles.tableModernTd}>{job?.job?.completed_date}</td>
                                            <td style={styles.tableModernTd}>{job?.job?.pickup_address}</td>
                                            <td style={styles.tableModernTd}>{job?.job?.drop_address}</td>
                                            <td style={styles.tableModernTd}>{job?.broker_name}</td>
                                            <td style={styles.tableModernTd}>{job?.rc_number}</td>
                                            <td style={{ ...styles.tableModernTd, textAlign: 'right', fontWeight: '600', color: '#1F2937' }}>${job?.job?.job_amount}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Expenses Details Section */}
                    <section style={{ marginTop: '3rem' }}>
                        <h2 style={styles.sectionHeader}><i className="fas fa-gas-pump" style={{ marginRight: '0.75rem', color: '#F97316' }}></i>Expenses</h2>
                        <div style={{ ...styles.infoCard, padding: 0, overflowX: 'auto' }}>
                            <table style={styles.tableModern}>
                                <thead style={styles.tableModernThead}>
                                    <tr>
                                        <th style={{ ...styles.tableModernTh, borderTopLeftRadius: '0.75rem' }}>Expense Type</th>
                                        <th style={{ ...styles.tableModernTh, textAlign: 'right', borderTopRightRadius: '0.75rem' }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expenses?.map((expense, index) => (
                                        <tr key={expense.id} style={index % 2 === 0 ? {} : { backgroundColor: '#F9FAFB' }}>
                                            <td style={styles.tableModernTd}>{expense?.name}</td>
                                            <td style={{ ...styles.tableModernTd, textAlign: 'right', color: '#DC2626', fontWeight: '600' }}>-${parseFloat(expense?.amount)?.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    {/* Footer Section */}
                    <footer style={{ textAlign: 'center', marginTop: '4rem', paddingTop: '2rem', borderTop: '2px dashed #D1D5DB' }}>
                        <p style={{ color: '#4B5563', fontWeight: '600' }}>Thank you for your hard work and dedication!</p>
                        <p style={{ fontSize: '0.875rem', color: '#9CA3AF', marginTop: '0.5rem' }}>
                            Powered by <span style={{ fontWeight: 'bold', ...styles.truklexOrange }}>Truklex</span> | compliance covered
                        </p>
                    </footer>
                </div>
            </div>

        </div>
    );
});

export default PaydayStatement;